import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/blog/')({
  component: BlogIndexPage,
  head: () => ({
    meta: [
      { title: 'Blog — Uptrack Uptime Monitoring' },
      { name: 'description', content: 'Thoughts on uptime monitoring, alert fatigue, SaaS infrastructure, and building reliable services. By the Uptrack team.' },
      { property: 'og:title', content: 'Blog — Uptrack' },
      { property: 'og:description', content: 'Thoughts on uptime monitoring, alert fatigue, SaaS infrastructure, and building reliable services. By the Uptrack team.' },
      { property: 'og:image', content: 'https://uptrack.app/og/blog.png' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/blog' },
    ],
  }),
})

const LIVE_POSTS = new Set([
  '/blog/what-is-uptime-monitoring',
  '/blog/free-pro-for-nonprofits',
  '/blog/http-451-unavailable-for-legal-reasons',
  '/blog/how-to-check-if-a-website-is-down-from-multiple-locations',
  '/blog/dns-monitoring-guide',
  '/blog/what-is-mtbf-meaning-formula',
  '/blog/free-uptime-monitoring-europe-gdpr',
  '/blog/synthetic-monitoring-services-guide',
  '/blog/freshping-discord-integration-alternative',
  '/blog/22-check-locations',
  '/blog/uptrack-in-claude-connector-directory',
  '/blog/multi-region-consensus',
  '/blog/best-free-uptime-monitoring-tools',
  '/blog/why-alert-confirmation',
  '/blog/uptime-monitoring-indie-hackers',
  '/blog/what-is-uptime-sla',
  '/blog/downtime-seo-impact',
  '/blog/why-beam',
  '/blog/typst-for-pdf-generation',
])

const posts = [
  {
    slug: '/blog/what-is-uptime-monitoring',
    title: 'What Is Uptime Monitoring? How It Works and What to Check',
    description: 'Uptime monitoring checks your site from outside on a schedule and alerts you when it fails. How it works, what to check, and how often to check.',
    date: 'November 1, 2026',
    category: 'Education',
  },
  {
    slug: '/blog/free-pro-for-nonprofits',
    title: 'Uptrack Pro Is Free for Nonprofits and Charities',
    description: 'Registered nonprofits and charities get Pro free for 12 months. Claim it in one click when your domain is on our lists, or apply with your registry number.',
    date: 'October 27, 2026',
    category: 'Product',
  },
  {
    slug: '/blog/dns-monitoring-guide',
    title: 'DNS Monitoring: What to Monitor, and What an Uptime Tool Can See',
    description: 'Query, server and traffic monitoring explained. What to check in DNS (records, nameservers, expiry, DNSSEC) and where an outside check stops.',
    date: 'October 13, 2026',
    category: 'Education',
  },
  {
    slug: '/blog/what-is-mtbf-meaning-formula',
    title: 'What Is MTBF? Meaning, Formula and MTBF vs MTTR vs MTTF',
    description: 'MTBF stands for mean time between failures. The formula with a worked example, how it differs from MTTR and MTTF, and how to measure it.',
    date: 'October 13, 2026',
    category: 'Education',
  },
  {
    slug: '/blog/how-to-check-if-a-website-is-down-from-multiple-locations',
    title: "How to Check if a Website Is Down From Multiple Locations",
    description: "How to check if a website is down from multiple locations: why one location misleads, how to read each result, and what to do next. Free, no signup.",
    date: 'October 12, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/http-451-unavailable-for-legal-reasons',
    title: "HTTP 451 Unavailable For Legal Reasons: What the Error Means and What to Do",
    description: "HTTP 451 Unavailable For Legal Reasons (RFC 7725): what the error means, how it differs from 403 and 404, and how to check it from other places.",
    date: 'October 12, 2026',
    category: 'Education',
  },
  {
    slug: '/blog/freshping-discord-integration-alternative',
    title: 'Freshping Discord Integration: Alerts After Shutdown',
    description: 'Freshping shut down on March 6, 2026. What its integrations were, and how to get uptime alerts into Discord now, on a free plan or through any webhook.',
    date: 'October 12, 2026',
    category: 'Alternatives',
  },
  {
    slug: '/blog/synthetic-monitoring-services-guide',
    title: 'Synthetic Monitoring Services: How to Choose One',
    description: 'Synthetic monitoring runs scripted checks of your site from outside. What it covers, when plain uptime checks are enough, and how to choose a service.',
    date: 'October 12, 2026',
    category: 'Education',
  },
  {
    slug: '/blog/free-uptime-monitoring-europe-gdpr',
    title: 'Free Uptime Monitoring in Europe: Limits and GDPR',
    description: 'What free uptime monitoring includes in 2026, where your monitoring data is stored, and what to check for GDPR before you pick a free plan.',
    date: 'October 12, 2026',
    category: 'Education',
  },
  {
    slug: '/blog/22-check-locations',
    title: 'Checking Your Site From 24 Locations Without 24 Times the False Alarms',
    description: MONITOR_LOCATIONS.blogDescription,
    date: 'October 11, 2026',
    category: 'Product',
  },
  {
    slug: '/blog/uptrack-in-claude-connector-directory',
    title: 'Ask Claude Which of Your Sites Are Down',
    description: 'Uptrack is listed in Claude’s connector directory. Check a URL, read uptime and pause a monitor from a chat. Needs a paid plan.',
    date: 'October 9, 2026',
    category: 'Product',
  },
  {
    slug: '/blog/monitor-ai-inference-endpoints',
    title: 'Monitor Your AI Inference Endpoints — vLLM, Ollama, Replicate, and Beyond',
    description: 'Inference is 55% of AI cloud spend. vLLM /health only confirms the process is running — not that models are loaded. Here\'s how to monitor the full AI stack.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/monitor-saas-dependencies',
    title: 'Monitor Your SaaS Dependencies Before They Take You Down',
    description: 'Stripe had a 2hr 15min outage. SendGrid had 14 incidents in 90 days. Monitoring tools caught 101 incidents vendors never reported.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/monitor-staging-uat-servers',
    title: 'Monitor Your Staging and UAT Servers — Before They Break Your Sprint',
    description: 'Staging breaks silently. QA starts their day, staging is down, sprint blocked. Here\'s how to catch it before standup.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/monitor-serverless-databases',
    title: 'Monitor Supabase, Neon, and Turso — Your Database Can Scale to Zero and Not Come Back',
    description: 'Serverless databases scale to zero. That spin-up can fail. Fauna shut down entirely. Here\'s how to monitor the new database stack.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/iot-heartbeat-monitoring',
    title: 'IoT Heartbeat Monitoring: Know When Your Devices Go Silent',
    description: 'AWS IoT Events shuts down May 2026. The heartbeat pattern — device pings a URL, alert if it stops — is simpler and works everywhere.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/game-server-monitoring',
    title: 'Game Server Monitoring — Know When Your Palworld, Minecraft, or Valheim Server Crashes',
    description: 'Self-hosted game servers crash at 3 AM. TCP port checks, Discord alerts, and heartbeat monitoring keep your community happy.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/monitor-serverless-edge-functions',
    title: 'Monitor Cloudflare Workers, Vercel Edge, and Serverless Functions From the Outside',
    description: 'You can\'t SSH into an edge function. External HTTP checks are the only way to know your serverless stack is actually working.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/cron-job-monitoring',
    title: 'Cron Job Monitoring: Know When Your Background Jobs Stop Running',
    description: 'Cron jobs fail silently. The heartbeat pattern — ping a URL on completion, alert if it stops — catches failures before data goes stale.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/monitor-mcp-servers',
    title: 'Monitor Your MCP Servers — The New Microservices',
    description: '1,600+ MCP servers and counting. If your MCP server goes down, Claude loses its tools mid-conversation. Here\'s how to monitor them.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/monitor-coolify-railway-flyio',
    title: 'Monitor Your Coolify, Railway, and Fly.io Deployments',
    description: 'PaaS platforms abstract infrastructure but still go down. "Git push and pray" is not a monitoring strategy.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/monitor-blockchain-rpc-endpoints',
    title: 'Monitor Your Ethereum and Solana RPC Endpoints',
    description: 'A healthy RPC endpoint has error rates below 0.1%. Block lag means stale prices in your DeFi app. Here\'s how to verify your provider\'s SLA.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/replace-datadog-simple-monitoring',
    title: 'Why 84% of Teams Are Replacing Datadog With Simpler Monitoring',
    description: '84% of teams are actively reducing observability costs. Most don\'t need full APM — they need to know if their site is up.',
    date: 'April 10, 2026',
    category: 'Opinion',
  },
  {
    slug: '/blog/monitor-internal-tools-tailscale',
    title: 'Monitor Internal Tools Behind Tailscale and Zero-Trust Networks',
    description: 'Internal tools break silently. Nobody notices until a support agent can\'t look up a customer. Here\'s how to monitor private endpoints.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/status-pages-build-trust',
    title: 'Status Pages That Build Customer Trust — Why Every SaaS Needs One',
    description: 'Customers forgive downtime. They don\'t forgive silence. A public status page turns panic into transparency.',
    date: 'April 10, 2026',
    category: 'Opinion',
  },
  {
    slug: '/blog/aws-iot-events-alternative',
    title: 'AWS IoT Events Is Shutting Down — Here\'s Your Migration Path',
    description: 'AWS IoT Events discontinued May 20, 2026. Replace 5 AWS services with 1 HTTP heartbeat request. Migration guide inside.',
    date: 'April 10, 2026',
    category: 'Use Cases',
  },
  {
    slug: '/blog/self-hosted-email-fleet',
    title: 'How We Built Unlimited Self-Hosted Email Delivery on the BEAM',
    description: 'From 3,000/month ESP limits to unlimited — Stalwart, gen_smtp connection reuse, dynamic process fleets, fuse circuit breakers, and :pg cross-node routing. Six problems, six BEAM-native solutions.',
    date: 'April 9, 2026',
    category: 'Engineering',
  },
  {
    slug: '/blog/multi-region-consensus',
    title: 'How We Built Multi-Region Check Consensus on the BEAM',
    description: 'How several check locations confirm a failure before Uptrack alerts (archived; written for the Elixir version).',
    date: 'April 5, 2026',
    category: 'Engineering',
  },
  {
    slug: '/blog/best-free-uptime-monitoring-tools',
    title: '7 Best Free Uptime Monitoring Tools in 2026 (Tested & Compared)',
    description: 'We tested the top free uptime monitoring tools. Compare check intervals, alert channels, false alarm handling, and what each free plan actually gives you.',
    date: 'April 4, 2026',
    category: 'Comparison',
  },
  {
    slug: '/blog/why-alert-confirmation',
    title: 'Why Most Uptime Alerts Are Noise — And How to Fix It',
    description: 'Your phone buzzes at 3am. DNS timeout. By the time you open your laptop, it\'s already resolved. Here\'s why consecutive-check confirmation changes everything.',
    date: 'April 1, 2026',
    category: 'Engineering',
  },
  {
    slug: '/blog/uptime-monitoring-indie-hackers',
    title: 'Uptime Monitoring for Indie Hackers — What You Actually Need',
    description: 'You don\'t need Datadog. You need simple, affordable monitoring that doesn\'t wake you up for nothing.',
    date: 'April 1, 2026',
    category: 'Opinion',
  },
  {
    slug: '/blog/what-is-uptime-sla',
    title: 'What Is a Good Uptime SLA? 99.9% vs 99.99% Explained',
    description: 'What those nines actually mean in real downtime minutes, and how your monitoring interval affects detection.',
    date: 'April 1, 2026',
    category: 'Education',
  },
  {
    slug: '/blog/downtime-seo-impact',
    title: 'How Website Downtime Affects Your SEO Rankings',
    description: 'Google crawl errors during downtime can deindex your pages. Here\'s how monitoring helps prevent SEO damage.',
    date: 'April 1, 2026',
    category: 'SEO',
  },
  {
    slug: '/blog/why-beam',
    title: 'Why We Chose BEAM for Uptime Monitoring',
    description: 'Archived: why we first built Uptrack on the BEAM VM. Written for the Elixir version; since June 2026 the backend is Rust.',
    date: 'April 1, 2026',
    category: 'Engineering',
  },
  {
    slug: '/blog/typst-for-pdf-generation',
    title: 'Generate PDFs Without Launching a Browser',
    description: 'Typst generates production-quality PDFs in milliseconds without Puppeteer or wkhtmltopdf. Here\'s why we use it for invoice generation in 9invoice.',
    date: 'April 18, 2026',
    category: 'Engineering',
  },
]

function BlogIndexPage() {
  return (
    <div className="container mx-auto px-4 py-12 md:py-16">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">The Latest from Uptrack</h1>
        <div className="flex items-center gap-5 text-sm font-medium">
          <Link to="/guides" className="text-primary underline-offset-4 hover:underline">
            Guides
          </Link>
          <a href="/rss.xml" className="text-primary underline-offset-4 hover:underline">
            RSS feed
          </a>
        </div>
      </header>

      <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
        {posts.filter((post) => LIVE_POSTS.has(post.slug)).map((post) => (
          <Link
            key={post.slug}
            to={post.slug}
            className="group flex flex-col rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
          >
            <p className="text-xs text-muted-foreground">
              {post.category} · {post.date}
            </p>
            <h2 className="mt-2 text-[17px] font-semibold leading-snug text-foreground group-hover:text-primary">
              {post.title}
            </h2>
            <p className="mt-2 line-clamp-3 flex-1 text-[15px] leading-relaxed text-body">{post.description}</p>
            <span className="mt-4 inline-flex items-center text-sm font-medium text-primary">
              Read on <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  )
}
